import React, { useState, useEffect } from "react";
import {
  Search,
  FileText,
  AlertCircle,
  CheckCircle,
  Copy,
  Download,
  Flag,
  Shield,
  Lock,
  Activity,
  LucideIcon,
} from "lucide-react";

interface SearchResult {
  type?: "boilerplate" | "past";
  key?: string;
  title?: string;
  icon?: LucideIcon;
  content?: string;
  keywords?: string[];
  relevance: number;
}

// Mock knowledge base of approved answers
const knowledgeBase: { [key: string]: SearchResult } = {
  security: {
    title: "Security & Compliance",
    icon: Shield,
    content: `Our platform maintains SOC 2 Type II certification and undergoes annual third-party security audits. We implement industry-standard encryption (AES-256) for data at rest and TLS 1.3 for data in transit. Multi-factor authentication is required for all administrative access, and we maintain comprehensive audit logs for all system activities.`,
    keywords: [
      "security",
      "encryption",
      "SOC",
      "audit",
      "compliance",
      "authentication",
    ],
    relevance: 100,
  },
  privacy: {
    title: "Privacy & Data Protection",
    icon: Lock,
    content: `We are fully GDPR and CCPA compliant. Patient data is processed in accordance with HIPAA requirements, with signed Business Associate Agreements (BAAs) in place. Data retention policies ensure information is kept only as long as necessary, with secure deletion procedures. Patients maintain full control over their data with rights to access, correction, and deletion.`,
    keywords: [
      "privacy",
      "GDPR",
      "CCPA",
      "HIPAA",
      "data protection",
      "retention",
      "BAA",
    ],
    relevance: 90,
  },
  hl7fhir: {
    title: "HL7/FHIR Compliance",
    icon: Activity,
    content: `Our platform fully supports HL7 FHIR R4 standards for healthcare data interoperability. We provide RESTful APIs compliant with FHIR specifications, supporting key resources including Patient, Observation, Medication, and Encounter. Our implementation has been validated through IHE Connectathon testing and supports SMART on FHIR for secure third-party app integration.`,
    keywords: [
      "HL7",
      "FHIR",
      "interoperability",
      "API",
      "SMART",
      "healthcare",
      "integration",
    ],
    relevance: 80,
  },
};

// Mock past RFP responses
const pastResponses = [
  {
    question: "What is your disaster recovery plan?",
    answer:
      "We maintain a comprehensive disaster recovery plan with RPO of 4 hours and RTO of 8 hours. Data is replicated across multiple geographic regions with automated failover capabilities.",
    confidence: 95,
  },
  {
    question: "How do you handle data breaches?",
    answer:
      "Our incident response plan includes immediate containment, forensic analysis, stakeholder notification within 72 hours as required by regulation, and post-incident review to prevent recurrence.",
    confidence: 90,
  },
  {
    question: "What is your uptime guarantee?",
    answer:
      "We guarantee 99.9% uptime with comprehensive SLAs. Our infrastructure includes redundant systems, automated monitoring, and 24/7 support team coverage.",
    confidence: 98,
  },
];

export default function RFPAutoResponder() {
  const [rfpInput, setRfpInput] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [selectedBoilerplate, setSelectedBoilerplate] = useState([]);
  const [flaggedItems, setFlaggedItems] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [generatedResponse, setGeneratedResponse] = useState("");

  // Simulate knowledge search
  const searchKnowledge = (query: string) => {
    setIsProcessing(true);
    setTimeout(() => {
      const words = query.toLowerCase().split(" ");
      const results: SearchResult[] = [];

      // Search in knowledge base
      Object.entries(knowledgeBase).forEach(([key, item]) => {
        const relevance = item.keywords?.filter((keyword) =>
          words.some((word) => keyword.includes(word))
        ).length || 0;
        if (relevance > 0) {
          results.push({
            ...item,
            type: "boilerplate",
            key,
            relevance,
          } as SearchResult);
        }
      });

      // Search in past responses
      pastResponses.forEach((response, index) => {
        const questionWords = response.question.toLowerCase().split(" ");
        const answerWords = response.answer.toLowerCase().split(" ");
        const relevance = words.filter(
          (word) => questionWords.includes(word) || answerWords.includes(word)
        ).length;
        if (relevance > 0) {
          results.push({
            ...response,
            type: "past",
            key: `past-${index}`,
            relevance,
          } as SearchResult);
        }
      });

      // Sort by relevance
      results.sort((a, b) => b.relevance - a.relevance);
      setSearchResults(results.slice(0, 5));

      // Auto-flag items that need review
      const flags = [];
      if (
        query.toLowerCase().includes("pricing") ||
        query.toLowerCase().includes("cost")
      ) {
        flags.push("Pricing information requires sales team review");
      }
      if (
        query.toLowerCase().includes("custom") ||
        query.toLowerCase().includes("specific")
      ) {
        flags.push("Custom requirements need technical review");
      }
      if (
        query.toLowerCase().includes("legal") ||
        query.toLowerCase().includes("liability")
      ) {
        flags.push("Legal terms require legal department approval");
      }
      setFlaggedItems(flags);

      setIsProcessing(false);
    }, 1000);
  };

  // Generate response from selected items
  const generateResponse = () => {
    let response = "";

    // Add boilerplate sections
    selectedBoilerplate.forEach((key) => {
      if (knowledgeBase[key]) {
        response += `**${knowledgeBase[key].title}**\n\n${knowledgeBase[key].content}\n\n`;
      }
    });

    // Add past responses
    searchResults
      .filter((result) => result.type === "past" && result.confidence > 80)
      .forEach((result) => {
        response += `**${result.question}**\n\n${result.answer}\n\n`;
      });

    setGeneratedResponse(response);
  };

  useEffect(() => {
    generateResponse();
  }, [selectedBoilerplate, searchResults]);

  const handleSearch = () => {
    if (rfpInput.trim()) {
      searchKnowledge(rfpInput);
    }
  };

  const toggleBoilerplate = (key) => {
    setSelectedBoilerplate((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedResponse);
  };

  const downloadResponse = () => {
    const blob = new Blob([generatedResponse], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "rfp-response.txt";
    a.click();
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            RFP Auto-Responder
          </h1>
          <p className="text-gray-600">
            Paste RFP sections to retrieve approved answers and generate
            consistent responses
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input Section */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <FileText className="w-5 h-5 mr-2" />
                RFP Input
              </h2>
              <textarea
                value={rfpInput}
                onChange={(e) => setRfpInput(e.target.value)}
                placeholder="Paste RFP section or questions here..."
                className="w-full h-48 p-4 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                onClick={handleSearch}
                disabled={!rfpInput.trim() || isProcessing}
                className="mt-4 w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              >
                {isProcessing ? (
                  <>Processing...</>
                ) : (
                  <>
                    <Search className="w-5 h-5 mr-2" />
                    Search Knowledge Base
                  </>
                )}
              </button>
            </div>

            {/* Boilerplate Sections */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Boilerplate Sections
              </h2>
              <div className="space-y-3">
                {Object.entries(knowledgeBase).map(([key, item]) => {
                  const Icon = item.icon;
                  const isSelected = selectedBoilerplate.includes(key);
                  return (
                    <button
                      key={key}
                      onClick={() => toggleBoilerplate(key)}
                      className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                        isSelected
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <Icon
                            className={`w-5 h-5 mr-3 ${
                              isSelected ? "text-blue-600" : "text-gray-400"
                            }`}
                          />
                          <span
                            className={`font-medium ${
                              isSelected ? "text-blue-900" : "text-gray-700"
                            }`}
                          >
                            {item.title}
                          </span>
                        </div>
                        {isSelected && (
                          <CheckCircle className="w-5 h-5 text-blue-600" />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Flagged Items */}
            {flaggedItems.length > 0 && (
              <div className="bg-amber-50 rounded-lg border border-amber-200 p-6">
                <h3 className="text-lg font-semibold text-amber-900 mb-3 flex items-center">
                  <Flag className="w-5 h-5 mr-2" />
                  Items Requiring Review
                </h3>
                <ul className="space-y-2">
                  {flaggedItems.map((item, index) => (
                    <li key={index} className="flex items-start">
                      <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 mr-2 flex-shrink-0" />
                      <span className="text-amber-800">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Response Section */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  Generated Response
                </h2>
                <div className="flex space-x-2">
                  <button
                    onClick={copyToClipboard}
                    className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Copy to clipboard"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <button
                    onClick={downloadResponse}
                    className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Download response"
                  >
                    <Download className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-6 min-h-[400px]">
                {generatedResponse ? (
                  <div className="prose prose-sm max-w-none">
                    {generatedResponse.split("\n\n").map((paragraph, index) => {
                      if (
                        paragraph.startsWith("**") &&
                        paragraph.endsWith("**")
                      ) {
                        return (
                          <h3
                            key={index}
                            className="text-lg font-semibold text-gray-900 mt-4 mb-2"
                          >
                            {paragraph.slice(2, -2)}
                          </h3>
                        );
                      }
                      return (
                        paragraph && (
                          <p key={index} className="text-gray-700 mb-3">
                            {paragraph}
                          </p>
                        )
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-500 italic">
                    Generated response will appear here after searching...
                  </p>
                )}
              </div>
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Related Matches
                </h3>
                <div className="space-y-3">
                  {searchResults.map((result) => (
                    <div key={result.key} className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">
                            {result.title || result.question}
                          </p>
                          {result.confidence && (
                            <p className="text-sm text-gray-600 mt-1">
                              Confidence: {result.confidence}%
                            </p>
                          )}
                        </div>
                        {result.type === "past" && (
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                            Past Response
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
